import {
  type FunctionComponent,
  type ReactNode,
  useRef,
  useState,
  useEffect,
  useCallback,
} from 'react'
import styled from '@emotion/styled'
import { Text, Flex, spacing } from '@retailer-platform/shared-components/src/tds'
import { colors } from '@retailer-platform/shared-components'
import { useDomainMessages } from '../utils/domain/intl'

const optionRowStyles = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  padding: `${spacing.s8} ${spacing.s16}`,
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  textAlign: 'left' as const,
  '&:hover': { backgroundColor: colors.GRAYSCALE.X10 },
}

interface SelectableDropdownOptionProps {
  label: string
  isSelected: boolean
  onClick: () => void
  disabled?: boolean
}

export const SelectableDropdownOption: FunctionComponent<SelectableDropdownOptionProps> = ({
  label,
  isSelected,
  onClick,
  disabled,
}) => (
  <button
    css={{
      ...optionRowStyles,
      backgroundColor: isSelected ? colors.GRAYSCALE.X10 : 'transparent',
      ...(disabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
    }}
    onClick={onClick}
    disabled={disabled}
  >
    <Text typography="bodyRegular">{label}</Text>
    {isSelected && <span css={{ color: colors.PRIMARY.REGULAR }}>✓</span>}
  </button>
)

const DropdownPanel = styled.div({
  backgroundColor: colors.GRAYSCALE.X0,
  border: `1px solid ${colors.GRAYSCALE.X20}`,
  borderRadius: '8px',
  boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
  overflow: 'hidden',
})

export interface CategorizedResultSection {
  title: string
  icon: ReactNode
  emptyLabel: string
  items: ReactNode[]
  loading?: boolean
}

interface CategorizedResultsDropdownProps {
  sections: CategorizedResultSection[]
  loading: boolean
}

export const CategorizedResultsDropdown: FunctionComponent<CategorizedResultsDropdownProps> = ({
  sections,
  loading,
}) => {
  const messages = useDomainMessages({
    loading: 'customersAndOrdersDomain.common.loading',
  })

  const BOTTOM_PADDING = parseInt(spacing.s24, 10)
  const panelRef = useRef<HTMLDivElement>(null)
  const [maxHeight, setMaxHeight] = useState<number | undefined>(undefined)

  const recalc = useCallback(() => {
    if (panelRef.current) {
      const top = panelRef.current.getBoundingClientRect().top
      setMaxHeight(Math.max(200, window.innerHeight - top - BOTTOM_PADDING))
    }
  }, [])

  useEffect(() => {
    recalc()
    window.addEventListener('resize', recalc)
    return () => window.removeEventListener('resize', recalc)
  }, [recalc])

  return (
    <DropdownPanel ref={panelRef} css={{ maxHeight, overflowY: 'auto' }}>
      {loading && (
        <Flex.Row css={{ padding: `${spacing.s8} ${spacing.s16}` }}>
          <Text typography="bodyRegular" color="systemGrayscale60">
            {messages.loading}
          </Text>
        </Flex.Row>
      )}

      {!loading &&
        sections.map((section, index) => (
          <div
            key={section.title}
            css={index > 0 ? { borderTop: `1px solid ${colors.GRAYSCALE.X20}` } : undefined}
          >
            <Flex.Row
              gap={spacing.s8}
              css={{
                padding: `${spacing.s8} ${spacing.s16}`,
                borderBottom: `1px solid ${colors.GRAYSCALE.X20}`,
                alignItems: 'center',
              }}
            >
              {section.icon}
              <Text typography="bodyEmphasized">{section.title}</Text>
            </Flex.Row>

            {section.loading ? (
              <Flex.Row css={{ padding: `${spacing.s8} ${spacing.s16}` }}>
                <Text typography="bodyRegular" color="systemGrayscale60">
                  {messages.loading}
                </Text>
              </Flex.Row>
            ) : section.items.length === 0 ? (
              <Flex.Row css={{ padding: `${spacing.s8} ${spacing.s16}` }}>
                <Text typography="bodyRegular" color="systemGrayscale60">
                  {section.emptyLabel}
                </Text>
              </Flex.Row>
            ) : (
              section.items
            )}
          </div>
        ))}
    </DropdownPanel>
  )
}

export default CategorizedResultsDropdown
